/**
 * @fileoverview Everything one author owns, and removing one of them.
 *
 * NOT THE AUTHOR INDEX. That one is for READERS and drops private pages on
 * purpose, so that a stranger cannot even count them. This one is for the
 * OWNER, and it must list them: a page missing from your own list is a page you
 * cannot remove.
 *
 * A private page is listed by its plaintext name and nothing else until the
 * owner asks. Each title costs the signer one decryption, and a remote signer
 * may put a prompt on the owner's phone for every one of them; opening forty on
 * every visit to this screen would be forty prompts nobody asked for.
 */

import type { SignerInterface } from '@cloistr/auth/core';
import type { Event, EventTemplate } from 'nostr-tools';


import { parseCollectionEvent } from './collectionEvent';
import { readOwnerKey } from './collectionKeys';
import { parseIndexEvent } from './indexEvent';
import { PAGE_COLLECTION_KIND, PAGE_INDEX_KIND, PAGE_THEME_KIND, SHARE_KIND } from './kinds';
import { isPrivatePageEvent, openSealedPage } from './privatePage';
import { shareEventsIssued } from './shareCollection';
import { parseThemeEvent } from './theme';

import { fetchAll, publishEvent, relaysFor } from '@/services/nostr/relays';

/** NIP-09. */
const DELETION_KIND = 5;

/** The same ceiling the author index uses per kind. */
const PER_KIND_LIMIT = 200;

/** Listed in this order: pages first, because that is what most authors have. */
const OWNED_KINDS = [PAGE_INDEX_KIND, PAGE_COLLECTION_KIND, PAGE_THEME_KIND] as const;

export interface MyItem {
  kind: number;
  pubkey: string;
  identifier: string;
  /** `kind:pubkey:identifier`, the durable name. */
  coordinate: string;
  /** The version this list saw. A deletion names it alongside the address. */
  eventId: string;
  createdAt: number;
  /** Null for a private page whose title has not been revealed, or one that would not open. */
  title: string | null;
  private: boolean;
  /** Kept so a private title can be opened later without asking the relay again. */
  event: Event;
}

export async function loadMine(pubkey: string, hints: readonly string[] = []): Promise<MyItem[]> {
  const relays = relaysFor(hints);
  // ONE KIND PER FILTER. cloistr-relay refuses a filter it considers complex
  // with a bare CLOSED, which reads exactly like an author who owns nothing.
  const answers = await Promise.all(
    OWNED_KINDS.map((kind) =>
      fetchAll(relays, { kinds: [kind], authors: [pubkey], limit: PER_KIND_LIMIT })
    )
  );

  const items: MyItem[] = [];
  answers.forEach((events, i) => {
    const kind = OWNED_KINDS[i]!;
    const newest = new Map<string, Event>();
    for (const event of events) {
      // The relay was asked for one author and one kind. Hold it to that rather
      // than trusting it, as every other loader here does.
      if (event.pubkey !== pubkey || event.kind !== kind) continue;
      const identifier = event.tags.find((t) => t[0] === 'd' && typeof t[1] === 'string')?.[1];
      if (identifier === undefined) continue;
      const held = newest.get(identifier);
      if (!held || event.created_at > held.created_at) newest.set(identifier, event);
    }
    const rows = [...newest.entries()].map(([identifier, event]) =>
      toItem(kind, pubkey, identifier, event)
    );
    rows.sort((a, b) => b.createdAt - a.createdAt);
    items.push(...rows);
  });
  return items;
}

function toItem(kind: number, pubkey: string, identifier: string, event: Event): MyItem {
  const isPrivate = isPrivatePageEvent(event);
  return {
    kind,
    pubkey,
    identifier,
    coordinate: `${kind}:${pubkey}:${identifier}`,
    eventId: event.id,
    createdAt: event.created_at,
    title: isPrivate ? null : titleOf(kind, event),
    private: isPrivate,
    event,
  };
}

function titleOf(kind: number, event: Event): string | null {
  if (kind === PAGE_INDEX_KIND) return parseIndexEvent(event)?.title ?? null;
  if (kind === PAGE_COLLECTION_KIND) return parseCollectionEvent(event)?.title ?? null;
  return parseThemeEvent(event, kind)?.title ?? null;
}

/**
 * Open the title of every private page the signer is the owner of.
 *
 * The same owner-first path the page loader takes: the envelope on the page's
 * own event, then the sealed tags, then the one parser. Anything that does not
 * open keeps a null title rather than failing the whole list.
 */
export async function revealPrivateTitles(
  items: readonly MyItem[],
  signer: SignerInterface
): Promise<MyItem[]> {
  return Promise.all(
    items.map(async (item) => {
      if (!item.private || item.title !== null) return item;
      try {
        const key = await readOwnerKey(item.event.tags, item.coordinate, signer);
        if (!key) return item;
        const plain = openSealedPage(item.event, key);
        const title = plain ? (parseIndexEvent(plain)?.title ?? null) : null;
        return { ...item, title };
      } catch {
        return item;
      }
    })
  );
}

/**
 * Ask the relays to remove one of the author's own items.
 *
 * A REQUEST, NOT A RETRACTION. Relays we do not run may ignore it, and anyone
 * who already fetched, mirrored or was given the key to the page keeps what
 * they have. The screen says so before this is ever called.
 */
export async function removeMine(
  item: MyItem,
  signer: SignerInterface,
  hints: readonly string[] = []
): Promise<{ accepted: string[]; refused: { relay: string; reason: string }[]; sharesRemoved: number }> {
  // Refused before anything is signed: a deletion from the wrong key does
  // nothing on a relay, and asking the signer for it costs the author a prompt.
  const pubkey = await signer.getPublicKey();
  if (pubkey !== item.pubkey) {
    throw new Error('That is not yours to remove: it was published by a different key.');
  }

  // THE SHARES GO WITH IT. Each share event names its recipient in the clear,
  // so one left behind is a dated record of who the author gave the key to,
  // still on the relay after the page is gone. Only a private page or a site
  // has a key to share; a public page has none, so it costs no lookup. Finding
  // them costs one signer decryption per share this author has sent, because a
  // share does not say in the clear what it is for.
  const relays = relaysFor(hints);
  const shares =
    item.private || item.kind === PAGE_COLLECTION_KIND
      ? await shareEventsIssued(item.coordinate, signer, relays)
      : [];

  const template: EventTemplate = {
    kind: DELETION_KIND,
    // An address deletion covers every version up to its own timestamp. The
    // newest version can sit a little ahead of the clock, because saves inside
    // one second are stamped strictly increasing, so "now" alone could leave
    // exactly the version the author was looking at. The same holds for each
    // share.
    created_at: Math.max(
      Math.floor(Date.now() / 1000),
      item.createdAt,
      ...shares.map((s) => s.created_at)
    ),
    tags: [
      ['a', item.coordinate],
      ['e', item.eventId],
      ['k', String(item.kind)],
      ...shares.flatMap((s) => [
        ['a', `${SHARE_KIND}:${s.pubkey}:${s.tags.find((t) => t[0] === 'd')?.[1] ?? ''}`],
        ['e', s.id],
      ]),
      ...(shares.length > 0 ? [['k', String(SHARE_KIND)]] : []),
    ],
    content: '',
  };
  const signed = await signer.signEvent({ ...template, pubkey });
  const outcome = await publishEvent(relays, signed, (t) => signer.signEvent({ ...t, pubkey }));
  return { ...outcome, sharesRemoved: shares.length };
}
